import { memo, useMemo, useState } from "react";

import { featureCollection } from "@turf/helpers";
import { Layer, Source } from "react-map-gl/maplibre";

import { _useGeoJSONEditorData } from "@/features/geojson_editor/GeoJSONEditorProvider";
import { META } from "@/features/geojson_editor/meta";
import { Color } from "@/features/utils/color";

export const LAYER_EDITOR_SOURCE = "editor-layer-source";
export const LAYERID_EDITOR_VERTEX = "editor-layer-vertex";
export const LAYERID_EDITOR_LINE = "editor-layer-line";
export const LAYERID_EDITOR_FILL = "editor-layer-fill";
export const LAYERID_EDITOR_CENTERPOINT = "editor-layer-centerpoint";

const EditorColor = new Color("#1e90ff");

const EditorLayer = memo(({ zIndex }: { zIndex: string }) => {
  const { isActive: isEditorActive } = _useGeoJSONEditorData((currentMode) => {
    const features = currentMode.generateMetaFeatures();
    setCollection(featureCollection(features));
  });

  const [collection, setCollection] = useState(featureCollection([]));

  // Memoize paint properties to prevent unnecessary re-renders
  const linePaint = useMemo(
    () => ({
      "line-color": EditorColor.opacity(0.8).hex(),
      "line-dasharray": [2, 1],
      "line-width": 4,
    }),
    [],
  );

  const fillPaint = useMemo(
    () => ({
      "fill-color": EditorColor.opacity(0.2).hexa(),
    }),
    [],
  );

  const vertexPaint = useMemo(
    () => ({
      "circle-color": EditorColor.hex(),
      "circle-opacity": 1,
      "circle-pitch-alignment": "map" as const,
      "circle-pitch-scale": "map" as const,
      "circle-radius": 8,
      "circle-stroke-color": new Color("#fff").opacity(0.8).hex(),
      "circle-stroke-opacity": 1,
      "circle-stroke-width": 3,
    }),
    [],
  );

  const centerpointPaint = useMemo(
    () => ({
      "circle-color": new Color("#fff").opacity(0.8).hex(),
      "circle-opacity": 1,
      "circle-pitch-alignment": "map" as const,
      "circle-pitch-scale": "map" as const,
      "circle-radius": 4,
      "circle-stroke-color": EditorColor.hex(),
      "circle-stroke-opacity": 1,
      "circle-stroke-width": 2,
    }),
    [],
  );

  // Don't render anything if editor is not active
  if (!isEditorActive) {
    return null;
  }

  return (
    <>
      <Source
        id={LAYER_EDITOR_SOURCE}
        type="geojson"
        data={collection}
        promoteId={"id"}
      >
        {/* Line layer */}
        <Layer
          id={LAYERID_EDITOR_LINE}
          key={LAYERID_EDITOR_LINE}
          type="line"
          paint={linePaint}
          layout={{}}
          filter={["==", "metaType", META.line]}
          beforeId={zIndex}
        />

        {/* Fill layer */}
        <Layer
          id={LAYERID_EDITOR_FILL}
          key={LAYERID_EDITOR_FILL}
          type="fill"
          paint={fillPaint}
          layout={{}}
          filter={["==", "metaType", META.fill]}
          beforeId={zIndex}
        />

        {/* Vertex layer */}
        <Layer
          id={LAYERID_EDITOR_VERTEX}
          key={LAYERID_EDITOR_VERTEX}
          type="circle"
          paint={vertexPaint}
          layout={{}}
          filter={["==", "metaType", META.vertex]}
          beforeId={zIndex}
        />

        {/* Centerpoint layer */}
        <Layer
          id={LAYERID_EDITOR_CENTERPOINT}
          key={LAYERID_EDITOR_CENTERPOINT}
          type="circle"
          paint={centerpointPaint}
          layout={{}}
          filter={["==", "metaType", META.midpoint]}
          beforeId={zIndex}
        />
      </Source>
    </>
  );
});

EditorLayer.displayName = "EditorLayer";

export default EditorLayer;
